import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { User, MapPin, Package, Home } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { EmptyState } from "@/components/States";
import { toNeDigits } from "@/lib/constants";
import { Reviews, RatingSummary } from "@/components/Reviews";

const SellerProfile = () => {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/sellers/${id}`)
      .then((res) => setSeller(res.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-10 text-center text-[#6B7280]">{t("empty.loading")}</div>;
  if (!seller) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#DCE7E2] flex items-center justify-center shrink-0">
          <User className="w-8 h-8 text-[#123A2C]" />
        </div>
        <div>
          <h1 className="text-xl font-extrabold text-[#14251F]" data-testid="seller-name">{seller.name}</h1>
          {seller.role === "buyer" && <div className="text-xs text-[#6B7280]">{t("seller.buyerProfile")}</div>}
          {seller.rating && (
            <div className="mt-1">
              <RatingSummary average={seller.rating.average} count={seller.rating.count} />
            </div>
          )}
          {seller.district && (
            <div className="flex items-center gap-1 text-sm text-[#6B7280] mt-1">
              <MapPin className="w-4 h-4" /> {seller.district}
            </div>
          )}
          {seller.address && (
            <div className="flex items-center gap-1 text-sm text-[#6B7280] mt-1" data-testid="seller-address">
              <Home className="w-4 h-4" /> {seller.address}
            </div>
          )}
          {seller.role !== "buyer" && (
            <div className="flex items-center gap-1 text-sm text-[#123A2C] font-medium mt-1">
              <Package className="w-4 h-4" /> {toNeDigits(seller.listing_count, lang)} {t("seller.listings")}
            </div>
          )}
        </div>
      </div>

      {seller.role !== "buyer" && (
        <>
          <h2 className="text-lg font-bold text-[#14251F] mt-8 mb-4">{t("seller.listings")}</h2>
          {seller.products.length === 0 ? (
            <EmptyState message={t("seller.noOtherListings")} />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
              {seller.products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </>
      )}

      <Reviews
        targetType="user"
        targetId={seller.id}
        ownerId={seller.id}
        title={seller.role === "seller" ? t("reviews.sellerTitle") : t("reviews.userTitle")}
        onChange={({ average, count }) => setSeller((s) => (s ? { ...s, rating: { average, count } } : s))}
      />
    </div>
  );
};

export default SellerProfile;
